import { useEffect, useState } from 'react';

import { useMarkUnsaved } from './unsaved';

/**
 * A field's in-progress text, kept in step with the row underneath it.
 *
 * Both editable fields need the same three lines and the same non-obvious reason for
 * them: a save, a regeneration, or another reviewer's edit replaces what is underneath,
 * and a draft that does not follow is stale in a way nobody can see.
 *
 * Adjusted during render rather than in an effect — an effect paints the old text first
 * and then corrects it, which is the flash React documents this pattern to avoid. That is
 * the part worth having in one place: written twice, the second copy is where it becomes
 * a `useEffect`.
 */
export function useDraft(value: string | number): {
  draft: string;
  setDraft: (next: string) => void;
  reset: () => void;
} {
  const [draft, setDraft] = useState(String(value));
  const [saved, setSaved] = useState(value);

  if (saved !== value) {
    setSaved(value);
    setDraft(String(value));
  }

  // Whether this field is holding work the API has not been told about — GEN-55. Reported
  // rather than returned: the count belongs to the screen, and every caller would otherwise
  // pass it up through a prop the next field forgets.
  const mark = useMarkUnsaved();
  const dirty = draft !== String(value);

  useEffect(() => {
    if (!dirty) return;

    mark(1);

    // The cleanup covers unmounting mid-edit too — a scene removed, the lesson navigated
    // away from — which is no longer work anyone can save.
    return () => {
      mark(-1);
    };
  }, [dirty, mark]);

  return {
    draft,
    setDraft,
    reset: () => {
      setDraft(String(value));
    },
  };
}
